import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '../../contexts/AuthContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiBook, FiUser, FiLogOut, FiSettings, FiSearch, FiMenu, FiX } = FiIcons

const Header = ({ onSearch, searchQuery, setCurrentView, currentView }) => {
  const { user, profile, signOut, isGuest, isStaff } = useAuth()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isSearchVisible, setIsSearchVisible] = useState(false)

  const handleSignOut = async () => {
    await signOut()
    setIsMenuOpen(false)
  }

  const navigateTo = (view) => {
    setCurrentView(view)
    setIsMenuOpen(false)
  }

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="bg-white shadow-sm border-b border-white-400 sticky top-0 z-50 px-4 py-3"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <motion.div 
          whileHover={{ scale: 1.02 }}
          className="flex items-center space-x-2 shrink-0 cursor-pointer"
          onClick={() => navigateTo('home')}
        >
          <div className="bg-dark_cyan p-1.5 md:p-2 rounded-lg">
            <SafeIcon icon={FiBook} className="w-5 h-5 md:w-6 md:h-6 text-white" />
          </div>
          <div className="hidden sm:block">
            <h1 className="text-lg md:text-xl font-black text-space_indigo leading-tight uppercase tracking-tighter">Zora's House</h1>
          </div>
        </motion.div>

        <div className="hidden md:block flex-1 max-w-xl mx-4">
          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white-300 w-4 h-4" />
            <input 
              type="text" 
              placeholder="Search library..." 
              value={searchQuery}
              onChange={(e) => onSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white-500 border-2 border-white-400 rounded-xl focus:border-dark_cyan focus:ring-0 transition-all text-sm font-bold placeholder-white-300"
            />
          </div>
        </div>

        <div className="hidden md:flex items-center space-x-2">
          {!isGuest ? (
            <>
              <button 
                onClick={() => setCurrentView('dashboard')}
                className={`px-4 py-2 rounded-lg transition-all font-black text-xs uppercase tracking-widest ${currentView === 'dashboard' ? 'bg-dark_cyan text-white' : 'text-white-200 hover:text-dark_cyan'}`}
              >
                Dashboard
              </button>
              {isStaff && (
                <button 
                  onClick={() => setCurrentView('admin')}
                  className={`px-4 py-2 rounded-lg transition-all font-black text-xs uppercase tracking-widest ${currentView === 'admin' ? 'bg-hot_berry text-white' : 'text-white-200 hover:text-hot_berry'}`}
                >
                  Admin
                </button>
              )}
              <div className="h-6 w-px bg-white-400 mx-2" />
              <button onClick={handleSignOut} className="p-2 text-white-300 hover:text-hot_berry transition-colors">
                <SafeIcon icon={FiLogOut} className="w-5 h-5" />
              </button>
            </>
          ) : (
            <button 
              onClick={() => setCurrentView('auth')}
              className="bg-dark_cyan text-white px-6 py-2.5 rounded-xl hover:bg-dark_cyan-400 transition-all font-black text-xs uppercase tracking-widest shadow-lg shadow-dark_cyan/20"
            >
              Sign In
            </button>
          )}
        </div>

        <div className="flex md:hidden items-center space-x-2">
          <button onClick={() => setIsSearchVisible(!isSearchVisible)} className={`p-2 rounded-lg transition-colors ${isSearchVisible ? 'text-dark_cyan' : 'text-white-300'}`}>
            <SafeIcon icon={FiSearch} className="w-6 h-6" />
          </button>
          <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="p-2 text-white-300">
            <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="w-6 h-6" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isSearchVisible && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden mt-3 px-1 overflow-hidden"
          >
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white-300 w-4 h-4" />
              <input 
                type="text" 
                autoFocus 
                placeholder="Search books, authors..." 
                value={searchQuery}
                onChange={(e) => onSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white-500 border-2 border-white-400 rounded-xl focus:border-dark_cyan transition-all text-sm font-bold"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed inset-0 top-[61px] bg-white z-40 md:hidden p-6 flex flex-col"
          >
            <div className="space-y-4 flex-1">
              {!isGuest && (
                <div className="mb-8 p-4 bg-white-500 rounded-2xl border-2 border-white-400">
                  <div className="flex items-center space-x-3">
                    <div className="bg-dark_cyan-900 p-2.5 rounded-xl">
                      <SafeIcon icon={FiUser} className="w-6 h-6 text-dark_cyan" />
                    </div>
                    <div>
                      <p className="font-black text-space_indigo uppercase text-sm">{profile?.full_name || 'User'}</p>
                      <p className="text-[10px] font-black text-white-300 uppercase tracking-widest">{profile?.role || 'User'}</p>
                    </div>
                  </div>
                </div>
              )}
              
              <button 
                onClick={() => navigateTo('home')}
                className={`w-full text-left p-4 rounded-xl font-black uppercase tracking-widest text-xs flex items-center ${currentView === 'home' ? 'bg-space_indigo text-white' : 'text-white-200 bg-white-500 border border-white-400'}`}
              >
                <SafeIcon icon={FiBook} className="w-5 h-5 mr-3" />
                Library Collection
              </button>

              {!isGuest ? (
                <>
                  <button 
                    onClick={() => navigateTo('dashboard')}
                    className={`w-full text-left p-4 rounded-xl font-black uppercase tracking-widest text-xs flex items-center ${currentView === 'dashboard' ? 'bg-dark_cyan text-white shadow-lg shadow-dark_cyan/20' : 'text-white-200 bg-white-500 border border-white-400'}`}
                  >
                    <SafeIcon icon={FiUser} className="w-5 h-5 mr-3" />
                    Personal Dashboard
                  </button>

                  {isStaff && (
                    <button 
                      onClick={() => navigateTo('admin')}
                      className={`w-full text-left p-4 rounded-xl font-black uppercase tracking-widest text-xs flex items-center ${currentView === 'admin' ? 'bg-hot_berry text-white shadow-lg shadow-hot_berry/20' : 'text-white-200 bg-white-500 border border-white-400'}`}
                    >
                      <SafeIcon icon={FiSettings} className="w-5 h-5 mr-3" />
                      Admin Control
                    </button>
                  )}

                  <div className="mt-8">
                    <button 
                      onClick={handleSignOut}
                      className="w-full text-left p-4 rounded-xl font-black uppercase tracking-widest text-xs flex items-center text-hot_berry bg-hot_berry-900"
                    >
                      <SafeIcon icon={FiLogOut} className="w-5 h-5 mr-3" />
                      Sign Out
                    </button>
                  </div>
                </>
              ) : (
                <button 
                  onClick={() => navigateTo('auth')}
                  className="w-full p-5 bg-dark_cyan text-white rounded-2xl font-black uppercase tracking-widest text-sm shadow-xl shadow-dark_cyan/20"
                >
                  Sign In to Library
                </button>
              )}
            </div>
            
            <div className="text-center pb-8">
              <p className="text-[10px] font-black text-white-400 uppercase tracking-[0.2em]">Zora's House Library v1.0</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  )
}

export default Header